import { SBCAAS_CLASSIC_LABEL } from '@/constants/sbcaas';
import { useState } from 'react';
import {
    useNavigate,
    useLocation,
    useParams,
    Navigate,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useToast } from '@chakra-ui/react';
import { useQuery } from 'hooks/useCustomQuery';

import { axios } from '@/services/axios';
import { multiSelectDataMapper } from 'utils/utils';

import {
    LoadingIndicator,
    Alert,
    AlertIcon,
    AlertDescription,
} from '@/components/v4';

import FormBase from '@/components/forms/new/FormBase';
import BreadcrumbTrail from 'components/BreadcrumbTrail';

// import { chakraSelectDarkMode, defaultChakraSelectStyle } from '@/constants';
import { maxRedundancyOptions, toastSuccess } from './constants';

// Form field components
// import SBCaaSNameField from '../../serviceprofiles/edit/formFields/SBCaaSNameField';
import SBCaaSTrunkTypesField from '../../serviceprofiles/edit/formFields/SBCaaSTrunkTypesField';
import SBCaaSRegionsField from '../../serviceprofiles/edit/formFields/SBCaaSRegionsField';
import SBCaaSMaxChannelField from '../../serviceprofiles/edit/formFields/SBCaaSMaxChannelField';
import SBCaaSMaxRedundancyField from '../../serviceprofiles/edit/formFields/SBCaaSMaxRedundancyField';

export function SBCaaS() {
    const location = useLocation();
    const { state } = location;
    const navigate = useNavigate();
    const { id } = useParams();
    const toast = useToast();

    // Loading and error states
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const companyID = useSelector(
        (state) => state.navigationLists.currentCompany,
    );
    const { darkMode } = useSelector((state) => state.settings);

    // const chakraSelectDark = chakraSelectDarkMode(darkMode);
    // const chakraStyles = {
    //     outlineColor: 'gray.300',
    //     ...defaultChakraSelectStyle,
    //     ...chakraSelectDark,
    // };

    // Handle form submission
    const handleSubmit = async (values, actions) => {
        setIsLoading(true);
        setError(null);

        try {
            // Transform regions and trunk types to backend format
            const transformedRegions = values.regions.map((region) => ({
                id: region.value,
            }));
            const transformedTrunkTypes = values.trunkTypes.map(
                (trunkType) => ({
                    id: trunkType.value,
                }),
            );

            const params = {
                id: selectedServiceData.id,
                regions: transformedRegions,
                trunkTypes: transformedTrunkTypes,
                maxChannel: Number(values.maxChannel),
                maxRedundancy: values.maxRedundancy,
                companyID: companyID,
                name: values.name,
            };

            await axios.put('/Service/SBCaaS', params);

            setTimeout(() => {
                toast(toastSuccess);
                navigate('/dashboard-services');
            }, 1000);
        } catch (error) {
            setError(
                error?.response?.data?.message ||
                    'An error occurred while updating the service.',
            );
            actions.setSubmitting(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackButton = () => {
        navigate('/dashboard-services');
    };

    const { data: availableSbcRegions = [], isLoading: regionsLoading } =
        useQuery({
            queryKey: ['sbcaasServiceRegions', companyID],
            queryFn: async () => {
                const result = await axios.get(
                    `/Services/SBCaaS/Company/${companyID}/Regions/Available`,
                );
                return result.data.map(multiSelectDataMapper);
            },
            enabled: Boolean(companyID),
        });

    const { data: availableTrunkTypes = [], isLoading: trunkTypesLoading } =
        useQuery({
            queryKey: ['sbcaasServiceTrunkTypes', companyID],
            queryFn: async () => {
                const result = await axios.get(
                    `/Services/SBCaaS/Company/${companyID}/TrunkTypes/Available`,
                );
                return result.data.map(multiSelectDataMapper);
            },
            enabled: Boolean(companyID),
        });

    const { data: selectedServiceData, isLoading: serviceDataLoading } =
        useQuery({
            queryKey: ['sbcaasServiceData', id],
            queryFn: async () => {
                const result = await axios.get(`/Service/${id}`);
                return result.data;
            },
            enabled: Boolean(id),
        });

    if (!state?.serviceId) {
        return <Navigate to="/dashboard-services" />;
    }

    if (
        regionsLoading ||
        trunkTypesLoading ||
        serviceDataLoading ||
        !availableSbcRegions.length ||
        !availableTrunkTypes.length ||
        !selectedServiceData
    ) {
        return <LoadingIndicator />;
    }

    // Map values to options for form initialization
    const mapToOptions = (values, options) => {
        if (!values || !options) return [];
        if (Array.isArray(values)) {
            return (
                values
                    .map((v) =>
                        options.find((o) => o.value === (v.value ?? v.id ?? v)),
                    )
                    ?.filter(Boolean) || []
            );
        }
        return (
            options.find(
                (o) => o.value === (values.value ?? values.id ?? values),
            ) || null
        );
    };

    const initialValues = {
        name: selectedServiceData?.name || '',
        trunkTypes: mapToOptions(
            selectedServiceData?.trunkTypes,
            availableTrunkTypes,
        ),
        regions: mapToOptions(
            selectedServiceData?.regions,
            availableSbcRegions,
        ),
        maxChannel: selectedServiceData?.maxChannel || 10,
        maxRedundancy: selectedServiceData?.maxRedundancy || 1,
    };

    const breadCrumbTrail = [
        { label: 'Services', path: ['/dashboard-services', { index: 0 }] },
        { label: 'Edit SBCaaS Service', path: '' },
    ];

    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`Edit ${SBCAAS_CLASSIC_LABEL} Service`}
                subTitleHeader={true}
                initialValues={initialValues}
                finalFormSubmit={handleSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}>
                {() => [
                    <SBCaaSTrunkTypesField
                        key="trunkTypes"
                        options={availableTrunkTypes}
                        darkMode={darkMode}
                    />,
                    <SBCaaSRegionsField
                        key="regions"
                        options={availableSbcRegions}
                        darkMode={darkMode}
                    />,
                    <SBCaaSMaxChannelField
                        key="maxChannel"
                        darkMode={darkMode}
                    />,
                    <SBCaaSMaxRedundancyField
                        key="maxRedundancy"
                        options={maxRedundancyOptions}
                        darkMode={darkMode}
                    />,
                    error && (
                        <Alert
                            key="error"
                            status="error"
                            sx={{ marginTop: '1rem' }}>
                            <AlertIcon />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    ),
                ]}
            </FormBase>
        </>
    );
}
